import { makeAutoObservable, runInAction } from 'mobx'
import {
  getActiveTicketUnreadUserId,
  publishTicketUnreadInvalidation,
} from '../lib/unread-sync'
import { TicketsService } from '~/shared/api/services/tickets'
import {
  CreateTicketMessagePayload,
  CreateTicketPayload,
  TicketDetailsType,
  TicketListItemType,
  UpdateTicketPayload,
} from '~/shared/api/services/tickets/types'
import { handleAxiosError } from '~/shared/lib'
import { TicketsLoaders } from './types'

class TicketsStore {
  filters = {
    status: ['open', 'in_progress'] as string[],
  }

  tickets: TicketListItemType[] = []

  currentTicket: TicketDetailsType | null = null

  hasUnreadUpdates = false

  private handledRealtimeUpdateIds: string[] = []

  private unreadRevision = 0

  private unreadChanges = new Map<
    string,
    { revision: number; unread: boolean }
  >()

  private listRequestId = 0

  private ticketDetailsRequestId = 0

  private refreshRequested = false

  private refreshRequestedSilentForbidden = false

  loaders: TicketsLoaders = {
    tickets: false,
    loadMoreTickets: false,
    createTicket: false,
    ticketDetails: false,
    createTicketMessage: false,
    updateTicket: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof TicketsLoaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof TicketsLoaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  getTickets = async (options?: {
    action?: 'loadMoreTickets' | 'refresh'
    filters?: { status?: string[] }
    silentForbidden?: boolean
  }) => {
    const { action, filters, silentForbidden } = options || {}

    if (
      action === 'loadMoreTickets' &&
      (this.requestSettings.isAllLoaded ||
        this.loaders.tickets ||
        this.loaders.loadMoreTickets)
    ) {
      return
    }

    const loader = action === 'loadMoreTickets' ? action : 'tickets'
    this.listRequestId += 1
    const { listRequestId: requestId, unreadRevision } = this
    this.activeLoader(loader)

    try {
      const currentFilters = {
        ...this.filters,
        ...filters,
      }
      const params = {
        limit:
          action === 'refresh'
            ? Math.max(this.requestSettings.offset, this.requestSettings.limit)
            : this.requestSettings.limit,
        offset: action !== 'loadMoreTickets' ? 0 : this.requestSettings.offset,
        ...currentFilters,
      }

      const response = await TicketsService.getTickets({
        params: { ...params, limit: Math.min(params.limit, 100) },
        silentForbidden: action === 'refresh' && silentForbidden,
      })
      if (requestId !== this.listRequestId) return
      const loadedData = [...response.data]
      if (action === 'refresh') {
        for (
          let offset = 100;
          offset < params.limit && loadedData.length === offset;
          offset += 100
        ) {
          // Stop at a short page and avoid flooding the API after a long scroll.
          // eslint-disable-next-line no-await-in-loop
          const page = await TicketsService.getTickets({
            params: {
              ...params,
              offset,
              limit: Math.min(100, params.limit - offset),
            },
            silentForbidden,
          })
          if (requestId !== this.listRequestId) return
          loadedData.push(...page.data)
        }
      }
      const data = loadedData.map((ticket) => {
        const change = this.unreadChanges.get(ticket.id)
        return change && change.revision > unreadRevision
          ? { ...ticket, hasUnreadUpdates: change.unread }
          : ticket
      })

      if (action === 'loadMoreTickets') {
        runInAction(() => {
          this.tickets = [...this.tickets, ...data]
        })
      } else {
        runInAction(() => {
          this.tickets = data
        })
      }

      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < params.limit,
          offset:
            action === 'loadMoreTickets'
              ? this.requestSettings.offset + this.requestSettings.limit
              : params.limit,
        }
      })

      return data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      if (requestId === this.listRequestId) {
        this.closeLoader('tickets')
        this.closeLoader('loadMoreTickets')
        if (this.refreshRequested) {
          const refreshSilentForbidden = this.refreshRequestedSilentForbidden
          runInAction(() => {
            this.refreshRequested = false
            this.refreshRequestedSilentForbidden = false
          })
          this.refreshLoadedTickets({
            silentForbidden: refreshSilentForbidden,
          }).catch(() => undefined)
        }
      }
    }
  }

  refreshLoadedTickets = async (options?: { silentForbidden?: boolean }) => {
    if (this.loaders.tickets || this.loaders.loadMoreTickets) {
      this.refreshRequested = true
      this.refreshRequestedSilentForbidden =
        this.refreshRequestedSilentForbidden ||
        Boolean(options?.silentForbidden)
      return
    }
    if (!this.tickets.length) return
    await this.getTickets({ action: 'refresh', ...options })
  }

  setFilters = (filters: Partial<TicketsStore['filters']>) => {
    const statusesChanged =
      filters.status !== undefined &&
      (filters.status.length !== this.filters.status.length ||
        filters.status.some(
          (status, index) => status !== this.filters.status[index],
        ))

    this.filters = {
      ...this.filters,
      ...filters,
    }
    if (statusesChanged) {
      this.tickets = []
    }
    this.requestSettings = {
      ...this.requestSettings,
      offset: 0,
      isAllLoaded: false,
    }
  }

  createTicket = async (payload: CreateTicketPayload) => {
    this.activeLoader('createTicket')

    try {
      const response = await TicketsService.createTicket(payload)
      await this.getTickets()
      return response
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createTicket')
    }
  }

  getTicketById = async (
    ticketId: string,
    options?: { silentForbidden?: boolean },
  ) => {
    this.ticketDetailsRequestId += 1
    const requestId = this.ticketDetailsRequestId
    this.activeLoader('ticketDetails')

    try {
      const response = await TicketsService.getTicketById(ticketId, options)

      if (requestId !== this.ticketDetailsRequestId) {
        return response.data
      }

      runInAction(() => {
        this.currentTicket = response.data
      })

      return response.data
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      if (requestId === this.ticketDetailsRequestId) {
        this.closeLoader('ticketDetails')
      }
    }
  }

  syncUnreadUpdates = async () => {
    const revision = this.unreadRevision
    try {
      const response = await TicketsService.getUnreadUpdates({
        silentForbidden: true,
      })

      runInAction(() => {
        if (revision === this.unreadRevision) {
          this.hasUnreadUpdates = response.data.hasUnreadUpdates
        }
      })

      return response.data.hasUnreadUpdates
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    }
  }

  markUnreadUpdatesRead = async (
    ticketId: string,
    userId = getActiveTicketUnreadUserId(),
    options?: { silentForbidden?: boolean },
  ) => {
    const revision = this.unreadRevision
    try {
      const response = await TicketsService.markUnreadUpdatesRead(
        ticketId,
        options,
      )
      const needsSync = revision !== this.unreadRevision

      runInAction(() => {
        if (revision === this.unreadRevision) {
          this.hasUnreadUpdates = response.data.hasUnreadUpdates
        }
        const change = this.unreadChanges.get(ticketId)
        if (!change || change.revision <= revision) {
          this.setTicketUnread(ticketId, false)
        }
      })

      if (needsSync) this.syncUnreadUpdates().catch(() => undefined)
      if (userId) {
        publishTicketUnreadInvalidation({
          type: 'ticket-unread-invalidated',
          userId,
          ticketId,
          reason: 'read',
        })
      }

      return response.data.hasUnreadUpdates
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    }
  }

  private setTicketUnread = (ticketId: string, unread: boolean) => {
    this.unreadRevision += 1
    this.unreadChanges.set(ticketId, { revision: this.unreadRevision, unread })
    const ticket = this.tickets.find((item) => item.id === ticketId)
    if (ticket) ticket.hasUnreadUpdates = unread
  }

  registerRealtimeUpdate = (
    updateId: string,
    ticketId: string,
    hasUnreadUpdates: boolean,
  ) => {
    if (this.handledRealtimeUpdateIds.includes(updateId)) {
      return false
    }

    this.handledRealtimeUpdateIds = [
      updateId,
      ...this.handledRealtimeUpdateIds,
    ].slice(0, 100)
    this.unreadRevision += 1
    this.hasUnreadUpdates = hasUnreadUpdates
    this.setTicketUnread(ticketId, true)

    return true
  }

  resetUnreadUpdates = () => {
    this.hasUnreadUpdates = false
    this.handledRealtimeUpdateIds = []
    this.unreadChanges.clear()
    this.unreadRevision += 1
    this.listRequestId += 1
    this.tickets = []
    this.refreshRequested = false
    this.closeLoader('tickets')
    this.closeLoader('loadMoreTickets')
  }

  createTicketMessage = async (
    ticketId: string,
    payload: CreateTicketMessagePayload,
  ) => {
    this.activeLoader('createTicketMessage')

    try {
      const response = await TicketsService.createTicketMessage(
        ticketId,
        payload,
      )
      await Promise.all([
        this.getTicketById(ticketId),
        this.refreshLoadedTickets(),
      ])
      return response
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createTicketMessage')
    }
  }

  updateTicket = async (ticketId: string, payload: UpdateTicketPayload) => {
    this.activeLoader('updateTicket')

    try {
      const response = await TicketsService.updateTicket(ticketId, payload)
      await Promise.all([
        this.getTicketById(ticketId),
        this.refreshLoadedTickets(),
      ])
      return response
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('updateTicket')
    }
  }
}

export default TicketsStore
